import { getObjectDiff } from 'shared'
import { Event } from 'typings'
import type { Promotion } from '../components/typings'
import { ReadonlyURLSearchParams } from 'next/navigation'

export enum PromotionOrigin {
  DUPLICATE = 'duplicate',
  TEMPLATE = 'template',
  STANDARD = 'standard',
}

export const ORIGIN_EVENT_MAP: Record<PromotionOrigin, Event> = {
  [PromotionOrigin.DUPLICATE]: Event.PROMOTION_DUPLICATED_SAVED,
  [PromotionOrigin.TEMPLATE]: Event.PROMOTION_FROM_TEMPLATE_SAVED,
  [PromotionOrigin.STANDARD]: Event.PROMOTION_SAVED,
}

export interface PromotionEventData {
  new: boolean
  type: Promotion['type']
  attempt: number
  data: Promotion
  changes?: ReturnType<typeof getObjectDiff>
  duplicateId?: string
  templateId?: string
}

type SearchParamsGetter = (key: string) => string | null

export const getPromotionOrigin = (
  searchParams: ReadonlyURLSearchParams
): PromotionOrigin => {
  if (searchParams.get('duplicate') !== null) return PromotionOrigin.DUPLICATE
  if (searchParams.get('template') !== null) return PromotionOrigin.TEMPLATE
  return PromotionOrigin.STANDARD
}

interface BuildEventDataParams {
  data: Promotion
  initialPromotion: Promotion
  attempt: number
  origin: PromotionOrigin
  searchParams: ReadonlyURLSearchParams
}

/**
 * Builds the event data for the promotion event.
 * Add changes to the event data if the promotion is not new or if the origin is not standard.
 * Add duplicateId to the event data if the origin is duplicate.
 * Add templateId to the event data if the origin is template.
 * @param {BuildEventDataParams} params - The parameters for the event data.
 * @returns {PromotionEventData} The event data.
 */
export const buildPromotionEventData = ({
  data,
  initialPromotion,
  attempt,
  origin,
  searchParams,
}: BuildEventDataParams): PromotionEventData => {
  const isNew = !data.id
  const hasChangesToTrack = !isNew || origin !== PromotionOrigin.STANDARD

  return {
    new: isNew,
    type: data.type,
    attempt,
    data,
    ...(hasChangesToTrack && {
      changes: getObjectDiff(data, initialPromotion),
    }),
    ...(origin === PromotionOrigin.DUPLICATE && {
      duplicateId: searchParams.get('duplicate')!,
    }),
    ...(origin === PromotionOrigin.TEMPLATE && {
      templateId: searchParams.get('template')!,
    }),
  }
}
